import { Popover as MuiPopover, type PopoverProps } from '@mui/material';
import { r, s } from '@/theme/tokens';
import type { SxProps, Theme } from './types';
import type { PopoverOwnerState } from '@mui/material/Popover';
import type { ReactElement } from 'react';

type StandardPopoverProps = PopoverProps & {
  minWidth?: number | string;
  paperSx?: SxProps<Theme>;
};

type PopoverPaperSlotProps = NonNullable<PopoverProps['slotProps']>['paper'];

export const Popover = ({
  minWidth,
  paperSx,
  slotProps,
  ...rest
}: StandardPopoverProps): ReactElement => {
  const basePaperSx: SxProps<Theme> = (theme) => ({
    padding: s('none'),
    borderRadius: r('none'),
    border: `1px solid ${theme.app.colors.border}`,
    boxShadow: 'none',
    backgroundColor: theme.app.colors.surface,
    backgroundImage: 'none',
    '--Paper-overlay': 'none',
    '--Paper-shadow': 'none',
    ...(minWidth !== undefined ? { minWidth } : {}),
    '& .MuiMenuItem-root': {
      fontSize: theme.typography.subtitle1.fontSize,
      lineHeight: theme.typography.subtitle1.lineHeight,
      fontWeight: theme.typography.subtitle1.fontWeight,
    },
    '& .MuiMenuItem-root .MuiTypography-root, & .MuiMenuItem-root .MuiListItemText-primary': {
      fontSize: theme.typography.subtitle1.fontSize,
      lineHeight: theme.typography.subtitle1.lineHeight,
      fontWeight: theme.typography.subtitle1.fontWeight,
    },
    '& .MuiAutocomplete-option': {
      fontSize: theme.typography.subtitle1.fontSize,
      lineHeight: theme.typography.subtitle1.lineHeight,
      fontWeight: theme.typography.subtitle1.fontWeight,
    },
    '& .MuiAutocomplete-option .MuiTypography-root': {
      fontSize: theme.typography.subtitle1.fontSize,
      lineHeight: theme.typography.subtitle1.lineHeight,
      fontWeight: theme.typography.subtitle1.fontWeight,
    },
  });

  const resolvePaperSx = (slotSx?: SxProps<Theme>): SxProps<Theme> => {
    return [basePaperSx, slotSx, paperSx].filter(Boolean) as SxProps<Theme>;
  };

  const slotPaper = slotProps?.paper;
  const buildPaperProps = (ownerState: PopoverOwnerState): PopoverPaperSlotProps => {
    const resolved = typeof slotPaper === 'function' ? slotPaper(ownerState) : slotPaper;
    const resolvedSx = resolved?.sx;
    return {
      square: true,
      ...(resolved ?? {}),
      sx: resolvePaperSx(resolvedSx),
    } as PopoverPaperSlotProps;
  };

  return (
    <MuiPopover
      {...rest}
      slotProps={{
        ...slotProps,
        paper: buildPaperProps,
      }}
    />
  );
};
